import { Avatar } from "@common/components";
import { RichTextReadOnly } from "@common/components/RichTextReadOnly";
import { timeToPixels } from "@common/utils/time-pixel-converter";
import { useMantineTheme } from "@mantine/core";
import { Markup } from "@server/entities";
import { useHover } from "@use-gesture/react";
import { motion } from "framer-motion";
import React, { useState } from "react";

import { useTasks } from "../../../../../../contexts/Tasks/TaskContext";
import { getMarkupColor } from "../../../../../stemviewer/view/markup/components/MarkupStatusBadge";

interface MixdownMarkupProps {
  markup: Markup;
  duration: number;
  width: number;
}

export const MixdownMarkup: React.FC<MixdownMarkupProps> = ({
  markup,
  duration,
  width,
}) => {
  const { viewMarkup } = useTasks();
  const theme = useMantineTheme();
  const [hovered, setHovered] = useState(false);
  const bind = useHover(({ hovering }) => setHovered(!!hovering));

  if (!markup.start) return null;

  return (
    <>
      {markup.end && (
        <div
          data-testid="markup-region"
          className="opacity-0 md:opacity-100 absolute z-30 -top-2 h-[110%] transition"
          style={{
            background: hovered ? "rgba(255,46,46,0.6)" : "rgba(255,46,46,0.4)",
            // `left` used to carry a `+ 9` fudge that bridged the old
            // canvas's 8px CSS margin: markups were positioned as siblings
            // of the canvas against their shared outer container, while the
            // canvas's own drawn pixels started 8px inset within that same
            // container. Now that this renders inside WaveformLane's own
            // box (see ProjectMixdown.tsx), the waveform, the playhead and
            // this marker all share one coordinate origin with no inset to
            // bridge, so the fudge is dropped — verified algebraically:
            // with it removed, a marker's `left` equals WaveformLane's own
            // `timeToX(time, duration, width)` for the same timestamp.
            left: timeToPixels(markup.start, width, duration),
            // Reviewed alongside the `left` fix above and left as-is: this
            // is a span (start->end), not a position, so it isn't the
            // playhead/marker misalignment the coordinate-origin change
            // caused — shortening the highlight by 16px reads as an
            // intentional inset (so the shaded region doesn't butt up
            // against the bubble), and `left` already uses no such
            // deduction, so the two aren't obviously the same bug. Not
            // changed without stronger evidence it's wrong.
            width: ((markup.end - markup.start) / duration) * (width - 16),
          }}
        />
      )}

      <div
        {...bind()}
        data-testid="sv-markup-bubble"
        data-quick-assist-id="sv-markup-bubble"
        className="absolute bottom-1/2 translate-y-1/2 cursor-pointer"
        onClick={() => markup.type === "markup" && viewMarkup(markup.id)}
        style={{
          left: timeToPixels(markup.end || markup.start, width, duration),
          zIndex: hovered ? 50 : 40,
        }}
      >
        <motion.div
          className="relative bg-dark-900 rounded-[2rem] rounded-bl-none overflow-hidden p-2"
          transition={{
            type: "tween",
            duration: 0.15,
          }}
          style={{
            border:
              markup.type === "markup"
                ? `1px solid ${theme.colors[getMarkupColor(markup.status)][4]}`
                : `1px solid ${theme.colors.dark[9]}`,
          }}
        >
          <div className="flex items-center gap-3 h-full">
            <Avatar
              className="transition duration-[0.15s]"
              size="sm"
              user={markup.reporter}
            />

            {hovered && (
              <motion.div
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: { opacity: 1, width: "auto", height: "auto" },
                  collapsed: { opacity: 0, width: 0, height: 0 },
                }}
                transition={{ type: "tween", duration: 0.15 }}
              >
                <div className="flex flex-col gap-1 text-[1rem] max-w-[250px] pr-2 truncate">
                  <RichTextReadOnly value={markup.title} />
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
};
